/**
 * Embedding Service - MiniLM WebGPU
 * Generates text embeddings for semantic search
 */
import { pipeline } from '@huggingface/transformers';

class EmbeddingService {
    constructor() {
        this.embedder = null;
        this.isLoading = false;
        this.isReady = false;
    }

    /**
     * Initialize the embedding model
     */
    async init(onProgress = () => { }) {
        if (this.isLoading) return false;
        if (this.isReady) return true;

        this.isLoading = true;

        try {
            onProgress({ text: 'Chargement embeddings...', progress: 10 });

            // Use MiniLM for fast, small embeddings (384 dimensions)
            this.embedder = await pipeline(
                'feature-extraction',
                'Xenova/all-MiniLM-L6-v2',
                {
                    dtype: 'fp32',
                    device: 'webgpu',
                }
            );

            this.isReady = true;
            this.isLoading = false;
            onProgress({ text: 'Embeddings prêts !', progress: 100 });
            return true;
        } catch (error) {
            console.error('WebGPU embeddings failed, trying WASM:', error);

            try {
                onProgress({ text: 'Fallback WASM...', progress: 50 });

                this.embedder = await pipeline(
                    'feature-extraction',
                    'Xenova/all-MiniLM-L6-v2',
                    {
                        dtype: 'q8',
                        device: 'wasm',
                    }
                );

                this.isReady = true;
                this.isLoading = false;
                onProgress({ text: 'Embeddings prêts (WASM)', progress: 100 });
                return true;
            } catch (fallbackError) {
                this.isLoading = false;
                throw fallbackError;
            }
        }
    }

    /**
     * Generate embedding for a single text
     * @param {string} text - Text to embed
     * @returns {Float32Array} - 384-dimensional embedding vector
     */
    async embed(text) {
        if (!this.isReady) {
            throw new Error('Embedding service not initialized');
        }

        const result = await this.embedder(text, {
            pooling: 'mean',
            normalize: true,
        });

        // Extract the embedding as Float32Array
        return new Float32Array(result.data);
    }

    /**
     * Generate embeddings for multiple texts
     * @param {string[]} texts - Array of texts to embed
     * @param {function} onProgress - Progress callback
     * @returns {Float32Array[]} - Array of embeddings
     */
    async embedBatch(texts, onProgress = () => { }) {
        if (!this.isReady) {
            throw new Error('Embedding service not initialized');
        }

        const embeddings = [];
        for (let i = 0; i < texts.length; i++) {
            const embedding = await this.embed(texts[i]);
            embeddings.push(embedding);
            onProgress({ current: i + 1, total: texts.length });
        }

        return embeddings;
    }

    getStatus() {
        return {
            isLoading: this.isLoading,
            isReady: this.isReady
        };
    }
}

export const embeddingService = new EmbeddingService();
export default embeddingService;
